import Vue from 'vue';
import './plugins/axios';
import App from './App.vue';
import router from './router';
import store from './store';
import { createPinia, PiniaVuePlugin } from 'pinia';
import pkg from '../package.json';
import AppEnvironmentPlugin from './plugins/appEnvironment';

// @ts-ignore
import VueToastr from 'vue-toastr';
import PortalVue from 'portal-vue';

// Keep Bootstrap on the initial, same-origin stylesheet path. Loading it from a
// remote CDN after first paint changes home geometry and creates a large CLS.
import 'bootstrap/dist/css/bootstrap.min.css';
import '@/assets/css/home-critical.css';
import '@/assets/css/tailwind-home.css';
import '@/assets/js/tooltip.js';
import { setMode } from '@chrissgon/perfectui';
import { shouldRedirectConnectedWalletFromLogin } from '@/shared/walletSessionPolicy';
import { ensureGlobalStyles } from '@/shared/globalStyles';
import { ensureFontAwesomeStyles } from '@/shared/appIcons';

setMode('dark');

Vue.use(PiniaVuePlugin);
const pinia = createPinia();

Vue.config.productionTip = false;

Vue.use(PortalVue);
Vue.use(AppEnvironmentPlugin);

Vue.use(VueToastr, {
	defaultProgressBar: true,
	defaultPosition: 'toast-bottom-left',
});

// Register auth/login redirect guard after Pinia is initialized
router.beforeEach(async (to, _from, next) => {
	if (to.path !== '/') {
		void ensureFontAwesomeStyles();
	}

	if (to.path !== '/auth/login') return next();

	const { useWalletStore } = await import('@/pinia/walletStore');
	const walletStore = useWalletStore(pinia);
	const shouldRedirectHome = shouldRedirectConnectedWalletFromLogin({
		loginPath: to.path,
		redirectPath: to.query.redirect,
		isConnected: walletStore.isConnected,
		isBotWallet: walletStore.isBotWallet,
	});
	if (!shouldRedirectHome) return next();
	return next({ path: '/' });
});

router.afterEach((to, from) => {
	if (from.matched.length > 0) {
		void import('@/pinia/cacheStore').then(({ useCacheStore }) => {
			useCacheStore().setListState(false);
		});
	}

	Vue.nextTick(() => {
		document.title = `FluxBeam` + (to.name ? ` | ${to.name}` : '');
	});
});

console.log(`${pkg.name}@${pkg.version}`);

const mountApp = async (): Promise<void> => {
	if (window.location.pathname !== '/') {
		void ensureFontAwesomeStyles();
		await ensureGlobalStyles();
	} else {
		void ensureGlobalStyles();
	}

	new Vue({
		router,
		store,
		pinia,
		render: (h) => h(App),
	}).$mount('#app');
};

void mountApp();
